<script lang="ts">
	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Button, Text, Container, IconButton } from '@Shelly/shelly-design-system';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { energyPriceColorSchemes, type ColorSchemeKey } from '../../utils/colorSchemes';
	import VerivoxTariffsModal from '../../modals/tariffs/VerivoxTariffsModal.svelte';
	import EnergyTariffCard from '../card/EnergyTariffCard.svelte';
	import BenchmarkTariffInfoButton from '../benchmark/BenchmarkTariffInfoButton.svelte';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import { openExternalLink } from 'src/helpers/utilities';
	import VerivoxAttribution from '../../shared/VerivoxAttribution.svelte';
	import type { EnergyTariff } from 'src/types/EnergyTariff';
	// import type { VerivoxOffer } from '../../api/verivoxApi';
	import { transformOfferToTariff } from '../../utils/tariffTransformers';

	const dispatch = createEventDispatcher<{ dataReady: { hasData: boolean } }>();

	export let variant: 'light' | 'dark' = 'light';
	export let postalCode: string | undefined;
	export let locationId: string | null | undefined;
	export let totalUsage: number = 2500;
	export let subPartnerId: string = '5762';
	export let campaignId: string = '2';
	export let onDismiss: (() => void) | undefined;

	let isLoading = false;
	let showModal = false;
	let priceSpan: {
		minYearlyPrice?: { amount?: string; content?: { text?: string } };
		maxYearlyPrice?: { amount?: string; content?: { text?: string } };
	} | null = null;
	let totalResults: number = 0;
	let bestTariffs: EnergyTariff[] = [];
	let benchmarkTariff: EnergyTariff | null = null;
	let containerWidth = 0;
	let containerWrapper: HTMLElement;

	const currency = '€';

	let currentScheme: ColorSchemeKey = 'scheme1';

	let resizeObserver: ResizeObserver | null = null;

	onMount(() => {
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
		fetchPriceSpan();

		// Set up resize observer to track container width
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver((entries) => {
				for (const entry of entries) {
					containerWidth = entry.contentRect.width;
				}
			});
		}

		// Initial width update
		if (containerWrapper) {
			containerWidth = containerWrapper.clientWidth;
			if (resizeObserver) {
				resizeObserver.observe(containerWrapper);
			}
		}
	});

	onDestroy(() => {
		if (resizeObserver && containerWrapper) {
			resizeObserver.unobserve(containerWrapper);
			resizeObserver.disconnect();
		}
	});

	const updateContainerWidth = () => {
		if (containerWrapper) {
			containerWidth = containerWrapper.clientWidth;
			if (resizeObserver) {
				resizeObserver.observe(containerWrapper);
			}
		}
	};

	// Calculate how many tariffs to show based on container width
	$: visibleTariffsCount = containerWidth >= 768 ? 3 : 2;

	const fetchPriceSpan = async () => {
		let targetPostalCode = postalCode;
		let targetLocationId = locationId;

		if (!targetPostalCode || !targetLocationId) {
			const cached = energyPriceApi.getCachedLocation();
			if (cached) {
				targetPostalCode = targetPostalCode || cached.postalCode;
				targetLocationId = targetLocationId || cached.locationId;
			}
		}

		if (!targetPostalCode || !targetLocationId) {
			dispatch('dataReady', { hasData: false });
			return;
		}

		isLoading = true;

		try {
			const res = await energyPriceApi.fetchTariffs(targetPostalCode, targetLocationId);

			if (res?.isok && res?.data) {
				// Extract totalResultsPriceSpan from the API response
				priceSpan = res.data.totalResultsPriceSpan || null;
				totalResults = parseInt(res.data.totalResults || '0', 10);

				// Get raw offers
				const rawOffers = res.data.offers || [];

				// Transform offers - preserve order from API response
				const transformedOffers = rawOffers.map((offer: any) =>
					transformOfferToTariff(offer, { isSponsored: false, preferResponsiveMsp: true }),
				);

				// Filter out sponsored tariffs, preserving order
				const allTariffs = transformedOffers.filter((tariff) => !tariff.isSponsored);

				// Get best tariffs (top 3) - preserving API order
				bestTariffs = allTariffs.slice(0, 3);

				// Extract benchmark tariff if available
				if (res.data.benchmarkTariff) {
					benchmarkTariff = transformOfferToTariff(res.data.benchmarkTariff);
				} else {
					benchmarkTariff = null;
				}

				const hasData = !!priceSpan && (totalResults > 0 || transformedOffers.length > 0);
				dispatch('dataReady', { hasData });
			} else {
				priceSpan = null;
				totalResults = 0;
				bestTariffs = [];
				benchmarkTariff = null;
				dispatch('dataReady', { hasData: false });
			}
		} catch (error) {
			console.error('Error fetching price span:', error);
			priceSpan = null;
			totalResults = 0;
			bestTariffs = [];
			benchmarkTariff = null;
			dispatch('dataReady', { hasData: false });
		} finally {
			isLoading = false;
		}
	};

	const handleShowOffers = () => {
		showModal = true;
	};

	const handleModalClose = () => {
		showModal = false;
	};

	const handleExploreMore = () => {
		const targetPostalCode = postalCode || energyPriceApi.getCachedLocation()?.postalCode;
		const targetLocationId = locationId || energyPriceApi.getCachedLocation()?.locationId;

		if (targetPostalCode && targetLocationId) {
			// Use cached consumption if totalUsage is default, otherwise use provided totalUsage
			const cachedConsumption = energyPriceApi.getCachedConsumption();
			const usageToUse = cachedConsumption !== null ? cachedConsumption : totalUsage;

			const redirectUrl = buildVerivoxRedirectUrl(
				targetPostalCode,
				targetLocationId,
				usageToUse,
				subPartnerId,
				campaignId,
			);
			openExternalLink(redirectUrl);
		} else {
			openExternalLink('https://www.verivox.de/?tunnel-partner5762');
		}
	};

	$: if (postalCode && locationId) {
		fetchPriceSpan();
	}

	$: if (containerWrapper) {
		updateContainerWidth();
	}
</script>

{#if !isLoading && bestTariffs.length > 0}
	<Container class="mb-m mt-m" type="primary-10" padding="s">
		<div class="banner-wrapper" bind:this={containerWrapper}>
			<div class="banner-header">
				<div class="banner-text">
					<div class="banner-title-row">
						<Text styleType="h2" class="banner-title">
							{$format('energy_price_setup_prompt_title', {
								default: 'Günstige Stromtarife entdecken',
								locale: 'de',
							})}
						</Text>
						<BenchmarkTariffInfoButton {benchmarkTariff} {isLoading} />
						{#if onDismiss}
							<IconButton
								variant="secondary"
								size="s"
								style="margin-left: auto;"
								icon="fa-solid fa-times"
								on:click={onDismiss}
								aria-label={$format('button_close', { default: 'Close' })}
							/>
						{/if}
					</div>
					{#if totalResults > 0}
						<Text styleType="body-small" class="banner-subtitle">
							{$format('energy_price_banner_subtitle', {
								default: '{count} Tarife verfügbar',
								values: { count: totalResults.toString() },
								locale: 'de',
							})}
						</Text>
					{/if}
				</div>
			</div>
			<div class="banner-tariffs-grid mb-m">
				{#each bestTariffs.slice(0, visibleTariffsCount) as tariff, i}
					<EnergyTariffCard
						{tariff}
						variant="light"
						layout="condensed"
						{currency}
						postalCode={postalCode || ''}
						locationId={locationId || ''}
						{totalUsage}
						{subPartnerId}
						{campaignId}
						backgroundGradient={energyPriceColorSchemes.schemes[currentScheme]}
					/>
				{/each}
			</div>
			<div class="d-flex justify-content-end">
				<Button variant="primary" size="m" on:click={handleShowOffers}>
					{$format('button_show_offers', { default: 'Angebote anzeigen', locale: 'de' })}
				</Button>
			</div>
			<VerivoxAttribution
				on:click={(event) => {
					event.detail.preventDefault = true;
					handleExploreMore();
				}}
			/>
		</div>
	</Container>
{/if}

<!-- Modal with full VerivoxOptions -->
{#if showModal}
	<VerivoxTariffsModal
		bind:showModal
		postalCode={postalCode || ''}
		locationId={locationId || ''}
		{totalUsage}
	/>
{/if}

<style>
	.banner-wrapper {
		width: 100%;
		position: relative;
	}

	.dismiss-button:hover {
		background: rgba(255, 255, 255, 0.3);
		opacity: 1;
	}

	.dismiss-button:active {
		transform: scale(0.95);
	}

	.banner-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		position: relative;
		z-index: 1;
		margin-bottom: 16px;
	}

	.banner-text {
		flex: 1;
		min-width: 0;
	}

	.banner-title-row {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
		margin-bottom: 4px;
	}

	.banner-title {
		margin: 0;
		color: #ffffff;
		font-weight: 700;
		line-height: 1.3;
		flex: 1;
	}

	.banner-subtitle {
		margin: 0;
		color: rgba(255, 255, 255, 0.9);
		opacity: 0.9;
	}

	.banner-tariffs-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 12px;
		position: relative;
		z-index: 1;
	}

	@media (min-width: 768px) {
		.banner-tariffs-grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	@media (max-width: 768px) {
		.banner-header {
			flex-direction: column;
			align-items: stretch;
			gap: 12px;
		}

		.banner-title {
			font-size: 14px;
		}

		.banner-subtitle {
			font-size: 11px;
		}

		.banner-tariffs-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
